import { AfterViewInit, Component, Input, OnInit, HostListener } from '@angular/core';

declare let Bokeh_v373: any;

interface ThemeColors {
  background: string;
  plotBackground: string;
  gridLine: string;
  gridLineAlpha: number;
  border: string;
  lactate: ColorConfig;
  vo2: ColorConfig;
  bars: BarColorConfig;
  xAxis: AxisColorConfig;
}

interface ColorConfig {
  line: string;
  dots: string;
  axis: string;
  axisValues: string;
  error: string;
}

interface BarColorConfig {
  fill: string;
  fillAlpha: number;
  stroke: string;
  strokeAlpha: number;
  text: string;
  timeLabel: string;
}

interface AxisColorConfig {
  label: string;
  values: string;
  ticks: string;
  text: string;
}

interface Range {
  start: number;
  end: number;
}

interface GlobalRanges {
  lactate: Range;
  vo2: Range;
}

interface SyncGrids {
  ranges: GlobalRanges;
  lactateGridTicks: number[];
  vo2GridTicks: number[];
  timeTicks: number[][];
}

interface AxisConfig {
  showLeftAxis: boolean;
  showRightAxis: boolean;
}

interface BarData {
  start: number;
  end: number;
  time: string;
  value: number;
  unit: string;
  vo2tot: number;
}

interface ChartData {
  bars: BarData[];
  duration: number[];
  lactate_values: number[];
  vo2_values: number[];
  lactate_dots_x?: number[];
  lactate_dots_y?: number[];
  lactate_dots_error?: number[][];
  vo2_dots_x?: number[];
  vo2_dots_y?: number[];
  vo2_dots_error?: number[][];
  chart_height?: number;
  element_suffix?: string;
}

@Component({
  selector: 'app-multi-kinetics-graph-runs',
  templateUrl: './multi-kinetics-graph-runs.component.html',
  styleUrls: ['./multi-kinetics-graph-runs.component.scss'],
})
export class MultiKineticsGraphRunsComponent implements AfterViewInit, OnInit {
  @Input() public config: any[] = [];
  @Input() public elementSuffix?: string;

  private containerWidth: number = 0;
  private chartWidths: number[] = [];
  private chartDurations: number[] = [];
  private syncGrids: SyncGrids | null = null;
  public chartHeight: number;

  private readonly themes: Record<string, ThemeColors> = {
    light: {
      background: '#fbfbfb',
      plotBackground: '#fbfbfb',
      gridLine: '#e0e0e0',
      gridLineAlpha: 0.8,
      border: '#cccccc',
      lactate: {
        line: '#ff0000',
        dots: '#ff0000',
        axis: '#111111',
        axisValues: '#111111',
        error: '#ff0000',
      },
      vo2: {
        line: '#0000ff',
        dots: '#0000ff',
        axis: '#111111',
        axisValues: '#111111',
        error: '#0000ff',
      },
      bars: {
        fill: '#d3d3d3',
        fillAlpha: 0.3,
        stroke: '#808080',
        strokeAlpha: 0.5,
        text: '#000000',
        timeLabel: '#000000',
      },
      xAxis: {
        label: '#000000',
        values: '#000000',
        ticks: '#cccccc',
        text: '#000000',
      },
    },
    dark: {
      background: '#282929',
      plotBackground: '#000000',
      gridLine: '#555555',
      gridLineAlpha: 0.6,
      border: '#666666',
      lactate: {
        line: '#ff6b6b',
        dots: '#ff6b6b',
        axis: '#aaaaaa',
        axisValues: '#aaaaaa',
        error: '#ff6b6b',
      },
      vo2: {
        line: '#4dabf7',
        dots: '#4dabf7',
        axis: '#aaaaaa',
        axisValues: '#aaaaaa',
        error: '#4dabf7',
      },
      bars: {
        fill: '#404040',
        fillAlpha: 0.4,
        stroke: '#707070',
        strokeAlpha: 0.7,
        text: '#ffffff',
        timeLabel: '#cccccc',
      },
      xAxis: {
        label: '#aaaaaa',
        values: '#aaaaaa',
        ticks: '#666666',
        text: '#cccccc',
      },
    },
  };

  public currentTheme: string = (localStorage.getItem('theme') || 'light').toLowerCase();

  private get theme(): ThemeColors {
    return this.themes[this.currentTheme];
  }

  ngOnInit(): void {
    this.chartHeight = this.config?.[0]?.chart_height;
    this.calculateChartDurations();
    this.updateDimensions();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.calculateChartDurations();
      this.createChartContainers();
      this.updateDimensions();
      this.calculateSyncGrids();
      this.createMultiKineticsGraph();
    }, 100);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.calculateChartDurations();
    this.createChartContainers();
    this.updateDimensions();
    this.calculateSyncGrids();
    this.createMultiKineticsGraph();
  }

  private calculateChartDurations(): void {
    this.chartDurations = this.config.map((data) => {
      if (!data?.bars?.length) {
        return 300;
      }

      const allX: number[] = [...data.duration, ...(data.lactate_dots_x || []), ...(data.vo2_dots_x || [])];

      data.bars.forEach((bar: BarData) => {
        allX.push(bar.start, bar.end);
      });

      const minX: number = Math.min(...allX);
      const maxX: number = Math.max(...allX);
      const padding: number = (maxX - minX) * 0.05;

      return maxX + padding - Math.max(0, minX - padding);
    });
  }

  private createChartContainers(): void {
    let container = document.querySelector('.multi-charts_container') as HTMLElement;
    if (this.elementSuffix) {
      container = document.querySelector(`#multi-charts-${this.elementSuffix}`) as HTMLElement;
    }
    if (!container) {
      return;
    }

    container.innerHTML = '';
    this.config.forEach((_: ChartData, index: number) => {
      const chartElementId = this.elementSuffix ? `chart-${index}-${this.elementSuffix}` : `chart-${index}`;
      const div: HTMLDivElement = document.createElement('div');
      div.id = chartElementId;
      div.className = 'chart-wrapper';
      div.style.margin = '0';
      div.style.padding = '0';
      if (this.chartHeight) {
        // div.style.height = `${this.chartHeight}px`;
        div.style.height = 'inherit';
      }
      container.appendChild(div);
    });
  }

  private updateDimensions(): void {
    let container = document.querySelector('.multi-charts_container') as HTMLElement;
    if (this.elementSuffix) {
      container = document.querySelector(`#multi-charts-${this.elementSuffix}`) as HTMLElement;
    }
    if (!container) {
      return;
    }

    this.containerWidth = container.offsetWidth;
    this.chartWidths = this.calculateAdaptiveWidths();
  }

  private calculateAdaptiveWidths(): number[] {
    if (!this.chartDurations.length) {
      return [];
    }

    const numGraphs: number = this.config.length;
    const totalGaps: number = (numGraphs - 1) * 5;
    const leftLegendWidth: number = 55;
    const rightLegendWidth: number = 55;
    const availableWidth: number = this.containerWidth - totalGaps - leftLegendWidth - rightLegendWidth;

    const totalDurationUnits: number = this.chartDurations.reduce((sum: number, duration: number) => sum + duration, 0);

    const getMinWidth = (duration: number): number => 20;

    const minWidths: number[] = this.chartDurations.map((duration) => getMinWidth(duration));
    const totalMinWidth: number = minWidths.reduce((sum: number, width: number) => sum + width, 0);

    if (totalMinWidth > availableWidth) {
      const scaleFactor: number = availableWidth / totalMinWidth;
      return minWidths.map((width) => Math.max(Math.floor(width * scaleFactor), 80));
    }

    const widths: number[] = this.chartDurations.map((duration: number) => {
      const proportion: number = duration / totalDurationUnits;
      const proportionalWidth: number = Math.floor(availableWidth * proportion);
      return Math.max(proportionalWidth, 0);
    });

    const totalCalculatedWidth: number = widths.reduce((sum: number, width: number) => sum + width, 0);
    if (totalCalculatedWidth > availableWidth) {
      const scaleFactor: number = availableWidth / totalCalculatedWidth;
      return widths.map((width) => Math.floor(width * scaleFactor));
    }

    widths[0] += leftLegendWidth;
    widths[widths.length - 1] += rightLegendWidth;

    return widths;
  }

  private calculateConsistentTimeScale(): number[] {
    if (!this.chartDurations.length) {
      return [];
    }

    const numGraphs: number = this.config.length;
    const totalGaps: number = (numGraphs - 1) * 5;
    const availableWidth: number = this.containerWidth - totalGaps;

    const maxDuration: number = Math.max(...this.chartDurations);
    const minWidthReserved: number = numGraphs * 100;
    const remainingWidth: number = Math.max(availableWidth - minWidthReserved, availableWidth * 0.5);
    const pixelsPerSecond: number = remainingWidth / maxDuration;

    return this.chartDurations.map((duration) => {
      const calculatedWidth: number = Math.floor(duration * pixelsPerSecond) + 100;
      return Math.max(calculatedWidth, duration < 300 ? 100 : 120);
    });
  }

  public getDurationInfo(): any {
    return {
      durations: this.chartDurations,
      widths: this.chartWidths,
      containerWidth: this.containerWidth,
      totalDuration: this.chartDurations.reduce((sum: number, d: number) => sum + d, 0),
      ratios: this.chartDurations.map((d) => d / Math.max(...this.chartDurations)),
    };
  }

  private calculateSyncGrids(): void {
    if (!this.config.length) {
      return;
    }

    const ranges: GlobalRanges = this.getGlobalRanges();
    const lactateGridTicks: number[] = this.generateGridTicks(ranges.lactate.start, ranges.lactate.end, 0);
    const vo2GridTicks: number[] = this.mapProportionalTicks(lactateGridTicks, ranges.lactate, ranges.vo2);
    const timeTicks: number[][] = this.config.map((data: ChartData, index: number) =>
      this.generateDynamicTimeTicks(data, index)
    );

    this.syncGrids = { ranges, lactateGridTicks, vo2GridTicks, timeTicks };
  }

  private getGlobalRanges(): GlobalRanges {
    const allLactate: number[] = [];
    const allVO2: number[] = [];

    this.config.forEach((data) => {
      allLactate.push(...data.lactate_values, ...(data.lactate_dots_y || []));
      allVO2.push(...data.vo2_values, ...(data.vo2_dots_y || []), ...(data.bars?.map((b: BarData) => b.vo2tot) || []));

      if (data.lactate_dots_error?.length && data.lactate_dots_y) {
        data.lactate_dots_y.forEach((y: number, i: number) => {
          const err = data.lactate_dots_error![i];
          if (err) {
            allLactate.push(y + Math.abs(err[0] || 0), y - Math.abs(err[1] || 0));
          }
        });
      }
    });

    const createLactateRange = (values: number[]): Range => {
      const max: number = Math.max(...values);
      const maxRounded: number = Math.ceil(max / 2) * 2;
      const endValue: number = Math.max(maxRounded, 6);
      return { start: 0, end: endValue };
    };

    const createVO2Range = (values: number[], pad: number): Range => {
      const min: number = Math.min(...values);
      const max: number = Math.max(...values);
      const padding: number = Math.max((max - min) * 0.1, pad);
      return { start: 0, end: max + padding };
    };

    return {
      lactate: createLactateRange(allLactate),
      vo2: createVO2Range(allVO2, 5),
    };
  }

  private generateGridTicks(min: number, max: number, count: number): number[] {
    if (min === 0) {
      const ticks: number[] = [];
      for (let tick = 0; tick <= max; tick += 2) {
        ticks.push(tick);
      }
      return ticks;
    }

    const range: number = max - min;
    const rawStep: number = range / (count - 1);
    const magnitude: number = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized: number = rawStep / magnitude;

    const niceStep: number = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    const step: number = niceStep * magnitude;

    const ticks: number[] = [];
    const start: number = Math.floor(min / step) * step;
    for (let tick = start; tick <= max + step * 0.001; tick += step) {
      if (tick >= min) {
        ticks.push(Math.round(tick * 1000) / 1000);
      }
    }
    return ticks;
  }

  private mapProportionalTicks(lactateGrid: number[], lactateRange: Range, vo2Range: Range): number[] {
    const lactateSpan: number = lactateRange.end - lactateRange.start;
    const vo2Span: number = vo2Range.end - vo2Range.start;

    return lactateGrid.map((tick) => {
      const proportion: number = (tick - lactateRange.start) / lactateSpan;
      return Math.round((vo2Range.start + proportion * vo2Span) * 1000) / 1000;
    });
  }

  private generateDynamicTimeTicks(data: ChartData, index: number): number[] {
    const bar: BarData = data.bars[0];
    const allX: number[] = [
      ...data.duration,
      bar.start,
      bar.end,
      ...(data.lactate_dots_x || []),
      ...(data.vo2_dots_x || []),
    ];

    const min: number = Math.min(...allX);
    const max: number = Math.max(...allX);
    const padding: number = 0;
    const start: number = Math.max(0, min - padding);
    const end: number = max + padding;

    const chartCount: number = this.config.length;
    const chartWidth: number = this.chartWidths[index] || 200;

    let tickCount: number;

    if (chartCount <= 3) {
      tickCount = 3;
    } else if (chartCount <= 6) {
      tickCount = 2;
    } else if (chartWidth < 120) {
      tickCount = 1;
    } else {
      tickCount = 2;
    }

    if (tickCount === 1) {
      const middle: number = (start + end) / 2;
      return [middle];
    } else if (tickCount === 2) {
      return [start, end];
    } else {
      const middle: number = (start + end) / 2;
      return [start, middle, end];
    }
  }

  private createCustomTickFormatter(): any {
    try {
      return new Bokeh_v373.CustomJSTickFormatter({
        code: 'var m=Math.floor(tick/60),s=Math.floor(tick%60);return m+":"+(s<10?"0":"")+s;',
      });
    } catch (e) {
      return null;
    }
  }

  public createMultiKineticsGraph(): void {
    this.config.forEach((_: ChartData, i: number) => {
      const chartElementId = this.elementSuffix ? `chart-${i}-${this.elementSuffix}` : `chart-${i}`;
      const chart = document.getElementById(chartElementId);
      if (chart) {
        chart.innerHTML = '';
      }
    });
    this.config.forEach((data: ChartData, i: number) => this.createChart(data, i));
  }

  private getAxisConfig(index: number, total: number): AxisConfig {
    return total === 1
      ? { showLeftAxis: true, showRightAxis: true }
      : { showLeftAxis: index === 0, showRightAxis: index === total - 1 };
  }

  private parseTimeToSeconds(timeString: string): number {
    const parts: string[] = timeString.split(':');
    if (parts.length === 2) {
      const minutes: number = parseInt(parts[0], 10);
      const seconds: number = parseInt(parts[1], 10);
      return minutes * 60 + seconds;
    }

    if (!isNaN(Number(timeString))) {
      return Number(timeString);
    }

    console.warn(`Could not parse time string: ${timeString}`);
    return 300;
  }

  private applyAdaptiveXAxisFormatting(p: any, chartIndex: number): void {
    if (!p.xaxis) {
      return;
    }

    const chartWidth: number = this.chartWidths[chartIndex] || 200;
    if (chartWidth < 60) {
      p.xaxis.major_label_orientation = Math.PI / 2;
      p.xaxis.major_label_text_font_size = this.scaledFontPt(8);
      p.xaxis.major_label_standoff = 5;
      if (this.chartHeight) {
        p.height = this.chartHeight;
      } else {
        p.height = 506;
      }
    }
  }

  private calculateRanges(data: ChartData): { x: Range } {
    if (!data.bars?.length) {
      return { x: { start: 0, end: 300 } };
    }

    const allX: number[] = [...data.duration, ...(data.lactate_dots_x || []), ...(data.vo2_dots_x || [])];

    data.bars.forEach((bar: BarData) => {
      allX.push(bar.start, bar.end);
    });

    const minX: number = Math.min(...allX);
    const maxX: number = Math.max(...allX);
    const padding: number = 15;

    return {
      x: {
        start: Math.max(0, minX - padding),
        end: maxX + padding,
      },
    };
  }

  private createChart(data: ChartData, index: number): void {
    if (!this.syncGrids) {
      return;
    }

    const totalGraphs: number = this.config.length;
    const axisConfig: AxisConfig = this.getAxisConfig(index, totalGraphs);
    const ranges: { x: Range } = this.calculateRanges(data);
    const { lactateGridTicks, vo2GridTicks, timeTicks } = this.syncGrids;
    const globalRanges: GlobalRanges = this.syncGrids.ranges;

    const isFirstChart: boolean = index === 0;
    const isLastChart: boolean = index === totalGraphs - 1;

    const baseHeight = this.chartHeight ?? 500;
    const baseHeightOffset = 0.1 * baseHeight;
    const figureConfig: any = {
      width: this.chartWidths[index] || 200,
      height: this.chartWidths[index] < 60 ? baseHeight + baseHeightOffset : baseHeight,
      title: null,
      toolbar_location: null,
      tools: '',
      active_drag: null,
      active_scroll: null,
      active_tap: null,
      x_range: [ranges.x.start, ranges.x.end],
      extra_y_ranges: {},
      background_fill_color: this.theme.plotBackground,
      border_fill_color: this.theme.background,
      outline_line_color: this.theme.gridLine,
      outline_line_alpha: this.theme.gridLineAlpha,
      margin: [0, 0, 0, 0],
      min_border: 0,
      min_border_top: 0,
      min_border_bottom: 0,
      min_border_left: 0,
      min_border_right: 0,
    };

    if (axisConfig.showLeftAxis) {
      figureConfig.y_range = [globalRanges.lactate.start, globalRanges.lactate.end];
      figureConfig.y_axis_label = 'Lactate [mmol/l]';
      figureConfig.extra_y_ranges.vo2 = new Bokeh_v373.Range1d(globalRanges.vo2);
    } else {
      figureConfig.y_range = [globalRanges.vo2.start, globalRanges.vo2.end];
      figureConfig.y_axis_location = null;
      figureConfig.extra_y_ranges.lactate = new Bokeh_v373.Range1d(globalRanges.lactate);
      figureConfig.extra_y_ranges.vo2 = new Bokeh_v373.Range1d(globalRanges.vo2);
    }

    const p = new Bokeh_v373.Plotting.figure(figureConfig);

    const lactateGridTicker = new Bokeh_v373.FixedTicker({ ticks: lactateGridTicks });
    const vo2GridTicker = new Bokeh_v373.FixedTicker({ ticks: vo2GridTicks });
    const timeGridTicker = new Bokeh_v373.FixedTicker({ ticks: timeTicks[index] });

    p.xaxis.ticker = timeGridTicker;
    p.xgrid.ticker = timeGridTicker;

    if (totalGraphs > 6) {
      p.xaxis.major_label_text_font_size = this.scaledFontPt(8);
    } else if (totalGraphs > 4) {
      p.xaxis.major_label_text_font_size = this.scaledFontPt(9);
    } else {
      p.xaxis.major_label_text_font_size = this.scaledFontPt(10);
    }

    if (axisConfig.showLeftAxis) {
      p.yaxis.ticker = p.ygrid.ticker = lactateGridTicker;
      this.applyAxisTheme(p.yaxis, this.theme.lactate.axis, this.theme.lactate.axisValues, isFirstChart);

      const vo2Axis = new Bokeh_v373.LinearAxis({ y_range_name: 'vo2', ticker: vo2GridTicker });
      p.add_layout(vo2Axis, 'left');
      vo2Axis.visible = false;
    } else {
      p.yaxis.ticker = p.ygrid.ticker = vo2GridTicker;

      const lactateAxis = new Bokeh_v373.LinearAxis({ y_range_name: 'lactate', ticker: lactateGridTicker });
      p.add_layout(lactateAxis, 'left');
      lactateAxis.visible = false;
    }

    if (axisConfig.showRightAxis) {
      const vo2Formatter = new Bokeh_v373.CustomJSTickFormatter({
        code: 'return Math.round(tick).toString();',
      });

      const rightAxis = new Bokeh_v373.LinearAxis({
        axis_label: 'VO2 [ml/min/kg]',
        ticker: vo2GridTicker,
        formatter: vo2Formatter,
      });
      p.add_layout(rightAxis, 'right');
      this.applyAxisTheme(rightAxis, this.theme.vo2.axis, this.theme.vo2.axisValues, isLastChart);
    }

    this.applyAxisTheme(p.xaxis, this.theme.xAxis.label, this.theme.xAxis.values, true);

    const customFormatter = this.createCustomTickFormatter();
    if (customFormatter) {
      p.xaxis.formatter = customFormatter;
    }

    p.grid.grid_line_color = this.theme.gridLine;
    p.grid.grid_line_alpha = this.theme.gridLineAlpha;

    this.renderChartElements(p, data, axisConfig, globalRanges);
    this.applyAdaptiveXAxisFormatting(p, index);

    const chartElementId = this.elementSuffix ? `#chart-${index}-${this.elementSuffix}` : `#chart-${index}`;
    Bokeh_v373.Plotting.show(p, chartElementId);
  }

  private applyAxisTheme(axis: any, labelColor: string, valueColor: string, forceBlackLines: boolean = false): void {
    Object.assign(axis, {
      axis_label_text_color: labelColor,
      major_label_text_color: valueColor,
      axis_label_text_font_size: this.scaledFontPt(10),
      major_label_text_font_size: this.scaledFontPt(8),
      axis_line_color: forceBlackLines ? '#000000' : this.theme.border,
      major_tick_line_color: forceBlackLines ? '#000000' : this.theme.border,
      axis_label_text_font_style: 'normal',
    });
  }

  private renderChartElements(p: any, data: ChartData, axisConfig: AxisConfig, globalRanges: GlobalRanges): void {
    data.bars.forEach((bar: BarData) => {
      const barWidth: number = bar.end - bar.start;
      const barCenter: number = (bar.start + bar.end) / 2;
      const barHeight: number = bar.vo2tot;
      const barYCenter: number = barHeight / 2;

      p.rect([barCenter], [barYCenter], [barWidth], [barHeight], {
        fill_color: this.theme.bars.fill,
        fill_alpha: this.theme.bars.fillAlpha,
        line_color: this.theme.bars.stroke,
        line_width: 1,
        line_alpha: this.theme.bars.strokeAlpha,
        y_range_name: 'vo2',
      });

      const shouldHideElements: boolean = this.renderAdaptiveBarText(
        p,
        bar,
        barCenter,
        barYCenter,
        barWidth,
        barHeight,
        globalRanges,
        data
      );

      if (!shouldHideElements) {
        this.renderAdaptiveDurationText(p, bar, barCenter, barWidth, globalRanges, data);
      }
    });

    const vo2YRange: string | undefined = axisConfig.showRightAxis && !axisConfig.showLeftAxis ? undefined : 'vo2';
    p.line(data.duration, data.vo2_values, {
      line_width: 2,
      line_color: this.theme.vo2.line,
      line_alpha: 0.8,
      y_range_name: vo2YRange,
    });

    const lactateYRange: string | undefined = axisConfig.showLeftAxis ? undefined : 'lactate';
    if (axisConfig.showRightAxis && !axisConfig.showLeftAxis) {
      const scaleFactor: number =
        (globalRanges.vo2.end - globalRanges.vo2.start) / (globalRanges.lactate.end - globalRanges.lactate.start);
      const scaledLactate: number[] = data.lactate_values.map(
        (v: number) => globalRanges.vo2.start + (v - globalRanges.lactate.start) * scaleFactor
      );
      p.line(data.duration, scaledLactate, {
        line_width: 2,
        line_color: this.theme.lactate.line,
        line_alpha: 0.8,
      });
    } else {
      p.line(data.duration, data.lactate_values, {
        line_width: 2,
        line_color: this.theme.lactate.line,
        line_alpha: 0.8,
        y_range_name: lactateYRange,
      });
    }

    this.renderLactateErrorBars(p, data, axisConfig, globalRanges);
    this.renderVO2ErrorBars(p, data, axisConfig, globalRanges);
  }

  private renderVO2ErrorBars(p: any, data: ChartData, axisConfig: AxisConfig, globalRanges: GlobalRanges): void {
    if (!data.vo2_dots_x?.length) {
      return;
    }

    const chartWidth: number = this.chartWidths[this.config.indexOf(data)] || 400;
    const baseHorizontalUnit: number = chartWidth * 0.015;

    for (let i = 0; i < data.vo2_dots_x.length; i++) {
      const x: number = data.vo2_dots_x[i];
      let y: number = data.vo2_dots_y![i];
      let errorYRange: string | undefined = axisConfig.showRightAxis && !axisConfig.showLeftAxis ? undefined : 'vo2';
      let scaleFactor: number = 1;

      if (axisConfig.showLeftAxis && !axisConfig.showRightAxis) {
        scaleFactor =
          (globalRanges.lactate.end - globalRanges.lactate.start) / (globalRanges.vo2.end - globalRanges.vo2.start);
        y = globalRanges.lactate.start + (y - globalRanges.vo2.start) * scaleFactor;
        errorYRange = undefined;
      }

      let errUp: number = 0;
      let errDown: number = 0;
      let errHorizontal: number = 0;

      if (data.vo2_dots_error?.[i]?.length) {
        const err: number[] = data.vo2_dots_error[i];
        errUp = Math.abs(err[0]) || 0;
        errDown = Math.abs(err[1]) || 0;
        errHorizontal = Math.abs(err[2]) || 0;

        if (axisConfig.showLeftAxis && !axisConfig.showRightAxis) {
          errUp *= scaleFactor;
          errDown *= scaleFactor;
        }
      }

      const lineStyle = {
        line_width: 2,
        line_color: this.theme.vo2.error,
        y_range_name: errorYRange,
      };

      if (errUp > 0) {
        p.line([x, x], [y, y + errUp], lineStyle);
        const capWidth: number =
          errHorizontal > 0 ? Math.max(errHorizontal / 2, baseHorizontalUnit) : baseHorizontalUnit;
        p.line([x - capWidth, x + capWidth], [y + errUp, y + errUp], lineStyle);
      }

      if (errDown > 0) {
        p.line([x, x], [y, y - errDown], lineStyle);
        const capWidth: number =
          errHorizontal > 0 ? Math.max(errHorizontal / 2, baseHorizontalUnit) : baseHorizontalUnit;
        p.line([x - capWidth, x + capWidth], [y - errDown, y - errDown], lineStyle);
      }

      if (errHorizontal > 0) {
        p.line([x - errHorizontal, x + errHorizontal], [y, y], lineStyle);
        const baseVerticalUnit: number =
          errorYRange === 'vo2'
            ? (globalRanges.vo2.end - globalRanges.vo2.start) * 0.03
            : (globalRanges.lactate.end - globalRanges.lactate.start) * 0.03;
        const capHeight: number = Math.max(Math.max(errUp, errDown) * 0.2, baseVerticalUnit);
        p.line([x - errHorizontal, x - errHorizontal], [y - capHeight, y + capHeight], lineStyle);
        p.line([x + errHorizontal, x + errHorizontal], [y - capHeight, y + capHeight], lineStyle);
      }

      p.scatter([x], [y], {
        size: 5,
        marker: 'circle',
        fill_color: this.theme.vo2.dots,
        line_color: this.theme.vo2.dots,
        fill_alpha: 0.8,
        line_alpha: 0.8,
        y_range_name: errorYRange,
      });
    }
  }

  private renderLactateErrorBars(p: any, data: ChartData, axisConfig: AxisConfig, globalRanges: GlobalRanges): void {
    if (!data.lactate_dots_x?.length) {
      return;
    }

    const chartWidth: number = this.chartWidths[this.config.indexOf(data)] || 400;
    const baseHorizontalUnit: number = chartWidth * 0.015;

    for (let i = 0; i < data.lactate_dots_x.length; i++) {
      const x: number = data.lactate_dots_x[i];
      let y: number = data.lactate_dots_y![i];
      let errorYRange: string | undefined = axisConfig.showLeftAxis ? undefined : 'lactate';
      let scaleFactor: number = 1;

      if (axisConfig.showRightAxis && !axisConfig.showLeftAxis) {
        scaleFactor =
          (globalRanges.vo2.end - globalRanges.vo2.start) / (globalRanges.lactate.end - globalRanges.lactate.start);
        y = globalRanges.vo2.start + (y - globalRanges.lactate.start) * scaleFactor;
        errorYRange = undefined;
      }

      let errUp: number = 0;
      let errDown: number = 0;
      let errHorizontal: number = 0;

      if (data.lactate_dots_error?.[i]?.length) {
        const err: number[] = data.lactate_dots_error[i];
        errUp = Math.abs(err[0]) || 0;
        errDown = Math.abs(err[1]) || 0;
        errHorizontal = Math.abs(err[2]) || 0;

        if (axisConfig.showRightAxis && !axisConfig.showLeftAxis) {
          errUp *= scaleFactor;
          errDown *= scaleFactor;
        }
      }

      const lineStyle = {
        line_width: 2,
        line_color: this.theme.lactate.error,
        y_range_name: errorYRange,
      };

      if (errUp > 0) {
        p.line([x, x], [y, y + errUp], lineStyle);
        const capWidth: number =
          errHorizontal > 0 ? Math.max(errHorizontal / 2, baseHorizontalUnit) : baseHorizontalUnit;
        p.line([x - capWidth, x + capWidth], [y + errUp, y + errUp], lineStyle);
      }

      if (errDown > 0) {
        p.line([x, x], [y, y - errDown], lineStyle);
        const capWidth: number =
          errHorizontal > 0 ? Math.max(errHorizontal / 2, baseHorizontalUnit) : baseHorizontalUnit;
        p.line([x - capWidth, x + capWidth], [y - errDown, y - errDown], lineStyle);
      }

      if (errHorizontal > 0) {
        p.line([x - errHorizontal, x + errHorizontal], [y, y], lineStyle);
        const baseVerticalUnit: number = 0.25;
        const capHeight: number = Math.max(Math.max(errUp, errDown) * 0.2, baseVerticalUnit);
        p.line([x - errHorizontal, x - errHorizontal], [y - capHeight, y + capHeight], lineStyle);
        p.line([x + errHorizontal, x + errHorizontal], [y - capHeight, y + capHeight], lineStyle);
      }

      p.scatter([x], [y], {
        size: 5,
        marker: 'circle',
        fill_color: this.theme.lactate.dots,
        line_color: this.theme.lactate.dots,
        fill_alpha: 0.8,
        line_alpha: 0.8,
        y_range_name: errorYRange,
      });
    }
  }

  private renderAdaptiveBarText(
    p: any,
    bar: BarData,
    barCenter: number,
    barYCenter: number,
    barWidth: number,
    barHeight: number,
    globalRanges: GlobalRanges,
    data: ChartData
  ): boolean {
    const text: string = `${bar.value} ${bar.unit}`;
    const currentChartIndex: number = this.config.indexOf(data);
    const chartWidth: number = this.chartWidths[currentChartIndex] || 200;

    const estimatedTextWidth: number = text.length * 6;
    const ranges: { x: Range } = this.calculateRanges(data);
    const timeRange: number = ranges.x.end - ranges.x.start;
    const pixelsPerTimeUnit: number = chartWidth / timeRange;
    const barWidthInPixels: number = barWidth * pixelsPerTimeUnit;

    if (estimatedTextWidth <= barWidthInPixels * 0.9) {
      p.text([barCenter], [barYCenter], [text], {
        text_align: 'center',
        text_baseline: 'middle',
        text_font_size: this.scaledFontPt(10),
        text_color: this.theme.bars.text,
        y_range_name: 'vo2',
      });
      return false;
    } else if (this.canFitTwoLines(bar, barWidthInPixels, barHeight, globalRanges)) {
      const valueText: string = bar.value.toString();
      const unitText: string = bar.unit;
      const lineSpacing: number = barHeight * 0.02;
      const upperY: number = barYCenter + lineSpacing;
      const lowerY: number = barYCenter - lineSpacing;

      p.text([barCenter], [upperY], [valueText], {
        text_align: 'center',
        text_baseline: 'middle',
        text_font_size: this.scaledFontPt(10),
        text_color: this.theme.bars.text,
        y_range_name: 'vo2',
      });

      p.text([barCenter], [lowerY], [unitText], {
        text_align: 'center',
        text_baseline: 'middle',
        text_font_size: this.scaledFontPt(10),
        text_color: this.theme.bars.text,
        y_range_name: 'vo2',
      });
      return false;
    } else if (this.canFitRotatedText(text, barWidthInPixels, barHeight)) {
      p.text([barCenter], [barYCenter], [text], {
        text_align: 'center',
        text_baseline: 'middle',
        text_font_size: this.scaledFontPt(10),
        text_color: this.theme.bars.text,
        y_range_name: 'vo2',
        angle: Math.PI / 2,
        angle_units: 'rad',
      });
      return false;
    } else {
      this.hideAxisElementsForChart(p, currentChartIndex);
      return true;
    }
  }

  private renderAdaptiveDurationText(
    p: any,
    bar: BarData,
    barCenter: number,
    barWidth: number,
    globalRanges: GlobalRanges,
    data: ChartData
  ): void {
    const durationText: string = bar.time;
    const currentChartIndex: number = this.config.indexOf(data);
    const chartWidth: number = this.chartWidths[currentChartIndex] || 200;

    const estimatedTextWidth: number = durationText.length * 5.5;
    const ranges: { x: Range } = this.calculateRanges(data);
    const timeRange: number = ranges.x.end - ranges.x.start;
    const pixelsPerTimeUnit: number = chartWidth / timeRange;
    const barWidthInPixels: number = barWidth * pixelsPerTimeUnit;
    const timeYPos: number = globalRanges.vo2.start;

    if (estimatedTextWidth <= barWidthInPixels * 0.9) {
      p.text([barCenter], [timeYPos], [durationText], {
        text_align: 'center',
        text_baseline: 'bottom',
        text_font_size: this.scaledFontPt(9),
        text_color: this.theme.bars.timeLabel,
        y_range_name: 'vo2',
      });
    } else if (
      this.canFitDurationTwoLines(durationText, barWidthInPixels, globalRanges) ||
      this.canFitRotatedDurationText(durationText, barWidthInPixels, globalRanges)
    ) {
      p.text([barCenter], [timeYPos], [durationText], {
        text_align: 'center',
        text_baseline: 'bottom',
        text_font_size: this.scaledFontPt(9),
        text_color: this.theme.bars.timeLabel,
        y_range_name: 'vo2',
        angle: Math.PI / 2,
        angle_units: 'rad',
      });
    }
  }

  private canFitTwoLines(
    bar: BarData,
    barWidthInPixels: number,
    barHeight: number,
    globalRanges: GlobalRanges
  ): boolean {
    const valueText: string = bar.value.toString();
    const unitText: string = bar.unit;

    const valueWidth: number = valueText.length * 6;
    const unitWidth: number = unitText.length * 6;
    const maxLineWidth: number = Math.max(valueWidth, unitWidth);

    if (maxLineWidth > barWidthInPixels * 0.9) {
      return false;
    }

    const vo2Range: number = globalRanges.vo2.end - globalRanges.vo2.start;
    const minHeightForTwoLines: number = vo2Range * 0.3;

    return barHeight >= minHeightForTwoLines;
  }

  private canFitRotatedText(text: string, barWidthInPixels: number, barHeight: number): boolean {
    const textHeightWhenRotated: number = text.length * 6;
    const textWidthWhenRotated: number = 12;

    return textHeightWhenRotated <= barHeight * 0.9 && textWidthWhenRotated <= barWidthInPixels * 0.9;
  }

  private canFitDurationTwoLines(durationText: string, barWidthInPixels: number, globalRanges: GlobalRanges): boolean {
    const parts: string[] = durationText.split(':');
    if (parts.length !== 2) {
      return false;
    }

    const minutesText: string = parts[0];
    const secondsText: string = parts[1];

    const minutesWidth: number = minutesText.length * 5;
    const secondsWidth: number = secondsText.length * 5;
    const maxLineWidth: number = Math.max(minutesWidth, secondsWidth);

    if (maxLineWidth > barWidthInPixels * 0.9) {
      return false;
    }

    const vo2Range: number = globalRanges.vo2.end - globalRanges.vo2.start;
    const availableBottomSpace: number = vo2Range * 0.1;
    const neededSpace: number = vo2Range * 0.04;

    return availableBottomSpace >= neededSpace;
  }

  private canFitRotatedDurationText(
    durationText: string,
    barWidthInPixels: number,
    globalRanges: GlobalRanges
  ): boolean {
    const textHeightWhenRotated: number = durationText.length * 5.5;
    const textWidthWhenRotated: number = 10;

    if (textWidthWhenRotated > barWidthInPixels * 0.9) {
      return false;
    }

    const vo2Range: number = globalRanges.vo2.end - globalRanges.vo2.start;
    const availableBottomSpace: number = vo2Range * 0.1;

    return textHeightWhenRotated <= availableBottomSpace * 0.8;
  }

  private hideAxisElementsForChart(p: any, chartIndex: number): void {
    if (p.xaxis?.length > 0) {
      p.xaxis[0].major_label_text_font_size = '0pt';
      p.xaxis[0].major_tick_line_color = 'transparent';
      p.xaxis[0].minor_tick_line_color = 'transparent';
    }

    if (this.syncGrids?.timeTicks) {
      this.syncGrids.timeTicks[chartIndex] = [];
    }
  }

  private scaledFontPt(fontSize: number): string {
    const fontSizeScale = this.chartHeight ? this.chartHeight / 500 : 1;
    return `${Math.ceil(fontSizeScale * fontSize)}pt`;
  }
}
